import {Injectable} from '@angular/core';
import {SearchState} from '../../types/SearchState';
import {ToolsService} from '../tools.service';
import {DefaultValuesService} from '../default-values.service';
import {SearchStateFieldsEnum} from '../../enums/SearchEnums';
import {UserLocationState} from '../../types/UserLocationState';

@Injectable({
  providedIn: 'root',
})
export class SearchStateManagerService {
  constructor(
    private tools: ToolsService,
    private defaults: DefaultValuesService,
  ) {}

  public getStateAfterSoftReset(state: SearchState): SearchState {
    if (state == null) {
      return this.tools.clone(this.defaults.getDefaultSearchState());
    }
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    state.caregiverUnitTypeUrlPath = defaultState.caregiverUnitTypeUrlPath;
    state.careServiceTypeUrlPath = defaultState.careServiceTypeUrlPath;
    state.symptomUrlPath = defaultState.symptomUrlPath;
    state.placeUrlPath = defaultState.placeUrlPath;
    state.freeSearchTerm = defaultState.freeSearchTerm;
    state.sort = defaultState.sort;
    state.page = defaultState.page;
    state.pageSize = defaultState.pageSize;
    state.totalPages = defaultState.totalPages;
    state.openNow = defaultState.openNow;
    state.openWeekend = defaultState.openWeekend;
    state.customerFeatureType = defaultState.customerFeatureType;
    state.isValid = true;
    state.digitalPage = defaultState.page;
    state.timeslot = defaultState.timeslot;
    state.attributes = defaultState.attributes;
    state.urlPath = defaultState.urlPath;
    state.distance = null;
    state.locationSearch = false;
    state.lat = 0;
    state.lon = 0;
    state.holdResultsReload = false;
    state.tab = defaultState.tab;
    return state;
  }

  public updateSearchStateProperty(state: SearchState, propertyName: string, propertyValue: string | number | boolean) {
    if (state && state[propertyName] !== propertyValue) {
      state[propertyName] = propertyValue;
      state = this.getSearchStateConditionalUpdated(propertyName, state);
    }
    return state;
  }

  public calculateNumberOfSelectedFilters(searchState: SearchState): number {
    const defaultState: SearchState = this.tools.clone(this.defaults.getDefaultSearchState());
    let numberOfSelectedFilters = 0;
    if (searchState.customerFeatureType !== defaultState.customerFeatureType) {
      numberOfSelectedFilters++;
    }
    if (searchState.openNow !== defaultState.openNow) {
      numberOfSelectedFilters++;
    }
    if (searchState.openWeekend !== defaultState.openWeekend) {
      numberOfSelectedFilters++;
    }
    if (searchState.timeslot !== defaultState.timeslot) {
      numberOfSelectedFilters += searchState.timeslot.length;
    }
    numberOfSelectedFilters += this.calculateNumberOfSelectedAttributeFilters(searchState);
    return numberOfSelectedFilters;
  }

  public calculateNumberOfSelectedAttributeFilters(searchState: SearchState): number {
    return Object.keys(searchState.attributes).length;
  }

  private getSearchStateConditionalUpdated(propertyName: string, state: SearchState): SearchState {
    if (
      propertyName === SearchStateFieldsEnum.CAREPROVIDER_UNIT_TYPE ||
      propertyName === SearchStateFieldsEnum.CARE_SERVICE_TYPE ||
      propertyName === SearchStateFieldsEnum.SYMPTOM ||
      propertyName === SearchStateFieldsEnum.PLACE
    ) {
      state.freeSearchTerm = '';
    }
    if (propertyName === SearchStateFieldsEnum.FREE) {
      state.caregiverUnitTypeUrlPath = '';
      state.careServiceTypeUrlPath = '';
      state.symptomUrlPath = '';
      state.placeUrlPath = '';
      state.lat = 0;
      state.lon = 0;
      state.locationSearch = false;
      state.openNow = false;
      state.openWeekend = false;
      state.customerFeatureType = '';
    }
    if (propertyName === 'locationSearch') {
      this.getStateModifiedByLocationSearch(state);
    }
    return state;
  }

  private getStateModifiedByLocationSearch(state: SearchState): SearchState {
    if (state.locationSearch === true) {
      const userLocationState: UserLocationState = this.tools.clone(this.defaults.getDefaultUserLocationState());
      state.lat = userLocationState.lat;
      state.lon = userLocationState.lon;
    } else {
      state.lat = 0;
      state.lon = 0;
    }
    return state;
  }
}
